import { CachedRequest } from "../cached_request.js";
import { AuthSessionStore } from "../auth_session.js";
import { getLeaderboardBoard } from "../requests/leaderboard.requests.js";
import type { LeaderboardBoardDto, LeaderboardEntryDto, LeaderboardScope } from "../dto/leaderboard.dto.js";

// The leaderboard lives on the game server (BlockBlastServer): ranks come from
// its periodic snapshot of players.points, so there is nothing to submit from
// the client — stars are counted on level complete server-side.

export enum LeaderboardTab
{
  World   = "world",
  Country = "country",
  Friends = "friends",
}

export const LEADERBOARD_TABS: readonly LeaderboardTab[] = [ LeaderboardTab.World, LeaderboardTab.Country, LeaderboardTab.Friends ];

const TAB_SCOPE: Record<LeaderboardTab, LeaderboardScope> = {
  [LeaderboardTab.World]:   "global",
  [LeaderboardTab.Country]: "country",
  [LeaderboardTab.Friends]: "friends",
};

/** One leaderboard row as the screen consumes it. */
export interface BoardRow
{
  id: number;
  is_player: boolean;
  rank: number;
  score: number;
  name: string | null;
  level: number;
  avatar_id: number;
  facebook_avatar_url: string | null;
  telegram_photo_url: string | null;
  country: string;
  created_at: string | null;
}

export interface Board
{
  tab: LeaderboardTab;
  /** The server fell back to the world board (country too small or unknown). */
  fallback_to_world: boolean;
  snapshot_at: string | null;
  rows: BoardRow[];
}

function toRow( entry: LeaderboardEntryDto, player_id: number | null ): BoardRow
{
  return {
    id: entry.id,
    is_player: player_id !== null && entry.id === player_id,
    rank: entry.rank,
    score: entry.score,
    name: entry.nickname,
    level: entry.player_level,
    avatar_id: entry.avatar_id,
    facebook_avatar_url: entry.facebook_avatar_url,
    telegram_photo_url: entry.telegram_photo_url,
    country: entry.country ?? "",
    created_at: entry.created_at,
  };
}

// top + around + me, deduplicated by id, ordered by rank.
export function assembleBoard( tab: LeaderboardTab, dto: LeaderboardBoardDto, player_id: number | null ): Board
{
  const by_id = new Map<number, BoardRow>();
  for ( const entry of [ ...dto.top, ...dto.around ] )
    by_id.set( entry.id, toRow( entry, player_id ) );
  if ( dto.me )
    by_id.set( dto.me.id, toRow( dto.me, player_id ) );

  const rows = [ ...by_id.values() ].sort( ( a, b ) => a.rank - b.rank || a.id - b.id );
  return { tab, fallback_to_world: dto.fallback === "global", snapshot_at: dto.snapshot_at, rows };
}

// Boards change only when the server rebuilds its snapshot (every few minutes),
// so a reopened screen can reuse the last answer for a while. The next_refresh_at
// hint is not used for the TTL: a short fixed window keeps own live score fresh.
const BOARD_CACHE_COOLDOWN_MS = 30_000;

function fetcher( tab: LeaderboardTab ): () => Promise<Board>
{
  return async () =>
  {
    const dto = await getLeaderboardBoard( TAB_SCOPE[tab] );
    return assembleBoard( tab, dto, AuthSessionStore.getPlayerId() );
  };
}

const caches: Record<LeaderboardTab, CachedRequest<Board>> = {
  [LeaderboardTab.World]:   new CachedRequest( fetcher( LeaderboardTab.World ), BOARD_CACHE_COOLDOWN_MS ),
  [LeaderboardTab.Country]: new CachedRequest( fetcher( LeaderboardTab.Country ), BOARD_CACHE_COOLDOWN_MS ),
  [LeaderboardTab.Friends]: new CachedRequest( fetcher( LeaderboardTab.Friends ), BOARD_CACHE_COOLDOWN_MS ),
};

export function leaderboardBoard( tab: LeaderboardTab ): CachedRequest<Board>
{
  return caches[tab];
}

export function invalidateLeaderboardBoards(): void
{
  for ( const cache of Object.values( caches ) ) cache.invalidate();
}
